// The Item Catalog list's URL state: the vocabulary the page's controls offer,
// and the arithmetic the pager runs on. No React, no router, no wire faces —
// `lib/` sits below both graphs and must never import upward to reach a type
// (`client/docs/architecture.md`, "The dependency rule").
//
// That rule is why `CATALOG_ITEM_SORTS` is spelled again here rather than
// imported from the internal-api declaration that owns the wire face. The two
// spellings are pinned against each other by a drift test in the one tier that
// can see both sides (`@/api/contracts/catalog-sorts-drift.unit.test.ts`),
// exactly as `background-jobs-format`'s shapes are.
//
// Everything in here is total: no branch throws, and no input is rejected. The
// URL is user-editable, so "invalid" is an ordinary case — the search schema
// (`@/api/contracts/catalog`) drops what it cannot read, and these helpers
// clamp what survives. A list page that 500s on `?page=-4` is worse than one
// that shows page 1.

import { nextSortDirection, type SortDirection } from "./sort-direction";

/** The five orderings the sort select offers, in menu order. */
export const CATALOG_ITEM_SORTS = [
  "recommended",
  "most_used",
  "least_used",
  "newest",
  "oldest",
] as const;

export type CatalogItemSort = (typeof CATALOG_ITEM_SORTS)[number];

export const DEFAULT_CATALOG_SORT: CatalogItemSort = "recommended";

/**
 * The sort select's options — value and label together, so the menu order and
 * the wire vocabulary cannot drift into two lists.
 *
 * "Recommended" is the server's flag ordering rather than a relevance ranking:
 * free-text search is the search-parity slice, and the no-term ordering is
 * plain flag ordering (spec #238, "Internal endpoint inventory").
 */
export const CATALOG_ITEM_SORT_OPTIONS: readonly {
  value: CatalogItemSort;
  label: string;
}[] = [
  { value: "recommended", label: "Recommended" },
  { value: "most_used", label: "Most used" },
  { value: "least_used", label: "Least used" },
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

/**
 * The four spellings the *URL* uses for a sort, and the donor's exactly.
 *
 * The Spec Tool's list page writes `?sort=` straight from the grid's ordering —
 * ``${direction === 'asc' ? '' : '-'}${field}`` (`items-list-page.tsx:225`) —
 * so its vocabulary is the four `order_by` strings below, and "Recommended" is
 * spelled by *deleting* the param (`:229`). A value it does not recognise reads
 * as Recommended rather than failing (`:179`).
 *
 * Atlas's own vocabulary (`CATALOG_ITEM_SORTS`) stays what it is: it is the
 * wire face, pinned to Python's `ItemSortName`, and nobody reads it. The URL is
 * the surface a person copies, bookmarks and pastes between the two stacks, so
 * it is the one that has to be the donor's, character for character — a link
 * carried over from the Spec Tool has to sort the way it did there.
 */
export const CATALOG_SORT_URL_VALUES = [
  "-usage_count",
  "usage_count",
  "-created_at",
  "created_at",
] as const;

export type CatalogSortUrlValue = (typeof CATALOG_SORT_URL_VALUES)[number];

const SORT_BY_URL_VALUE: Readonly<Record<CatalogSortUrlValue, CatalogItemSort>> = {
  "-usage_count": "most_used",
  usage_count: "least_used",
  "-created_at": "newest",
  created_at: "oldest",
};

const URL_VALUE_BY_SORT: Readonly<Record<CatalogItemSort, CatalogSortUrlValue | undefined>> = {
  recommended: undefined,
  most_used: "-usage_count",
  least_used: "usage_count",
  newest: "-created_at",
  oldest: "created_at",
};

/**
 * The ordering a `?sort=` value asks for — Recommended when it asks for nothing
 * this page knows, which is the donor's reading of a stale or hand-edited link.
 */
export const catalogSortFromUrl = (value: CatalogSortUrlValue | undefined): CatalogItemSort =>
  value === undefined ? DEFAULT_CATALOG_SORT : SORT_BY_URL_VALUE[value];

/**
 * How an ordering is spelled in the address bar — `undefined` for Recommended,
 * so the param is dropped rather than written, as the donor drops it.
 */
export const catalogSortToUrl = (sort: CatalogItemSort): CatalogSortUrlValue | undefined =>
  URL_VALUE_BY_SORT[sort];

/** The three renderings of one result set. */
export const CATALOG_VIEWS = ["card", "list", "table"] as const;

export type CatalogView = (typeof CATALOG_VIEWS)[number];

export const DEFAULT_CATALOG_VIEW: CatalogView = "card";

/**
 * The view toggle's buttons, in render order — table, list, card.
 *
 * The order is the donor's (`items-list-page.tsx`'s `MuiButtonGroup`), which
 * runs densest-first; `card` is still the default view, it is simply the last
 * button rather than the first. The labels are the donor's `aria-label` /
 * `title` strings verbatim, because the donor's buttons show an icon and no
 * text — so those strings are the whole of what a user reads.
 *
 * Beside the sort options rather than in the toolbar, because they are the same
 * kind of thing: a vocabulary plus the words a user reads for it. Labels only —
 * the icons are React and belong with the component that draws them.
 */
export const CATALOG_VIEW_OPTIONS: readonly { value: CatalogView; label: string }[] = [
  { value: "table", label: "View as table" },
  { value: "list", label: "View as list" },
  { value: "card", label: "View as card tiles" },
];

/**
 * The page sizes the old stack's **menu** offered, unchanged.
 *
 * The menu, specifically — not the set of sizes the page will honour. The donor
 * reads the URL as `Number(searchParams.get('pageSize')) || 20`
 * (`items-list-page.tsx:175`) with no membership check, so a *link* may carry
 * any number; these four are what a *click* can produce. `MAX_CATALOG_PAGE_SIZE`
 * below is the bound on the former.
 */
export const CATALOG_PAGE_SIZES = [10, 20, 50, 100] as const;

/** A size the page-size menu can emit. A URL is not bounded to these. */
export type CatalogPageSize = (typeof CATALOG_PAGE_SIZES)[number];

/**
 * The largest page size a URL may ask for.
 *
 * Derived from the menu rather than written out, because that is the reasoning:
 * no modsy user can produce a bigger link by clicking, so the top menu value is
 * where parity for reachable values ends and an unbounded fetch would begin
 * (#527, owner ruling in PR #524's decision round, D2 = a). The donor's own
 * ceiling is server-side and higher — itemlib bounds `limit` to 500 — so a
 * hand-edited `?pageSize=250` shows 250 rows there and 100 here, a residual
 * divergence named on #527 rather than an oversight.
 */
export const MAX_CATALOG_PAGE_SIZE: number = Math.max(...CATALOG_PAGE_SIZES);

export const DEFAULT_CATALOG_PAGE_SIZE: CatalogPageSize = 20;

export const DEFAULT_CATALOG_PAGE = 1;

/**
 * The page that keeps the user roughly where they were when the size changes.
 *
 * Anchored on the first row currently on screen: page 3 of 20 starts at row 41,
 * which is page 5 once the size drops to 10. Resetting to page 1 instead would
 * throw away a deep scroll through 11k items for a control that changes only
 * how much of it is visible.
 *
 * **Written as the donor writes it — divide by the ratio, don't multiply out.**
 * `handlePageSizeChange` computes `Math.floor((page - 1) / (newSize /
 * pageSize)) + 1` (`items-list-page.tsx:277-278`), and the two forms are equal
 * in arithmetic but not in IEEE-754: from page 6 at 58 rows, the donor's
 * `5 / (10 / 58)` is `28.999999999999996` and floors to 28, landing on page 29,
 * while the exact `(5 * 58) / 10` is 29 and lands on 30. A different page is a
 * different ten rows and a different URL, so parity takes the donor's number —
 * float error included — rather than the arithmetically tidier one.
 *
 * Only an **off-menu current size** can produce that disagreement: across every
 * menu-to-menu combination the two forms agree exactly, which is why this
 * became reachable with #527 rather than having been wrong all along.
 */
export function pageAfterPageSizeChange(
  page: number,
  currentPageSize: number,
  nextPageSize: number,
): number {
  // The donor has no `max(1, …)`; this one is unreachable defence — the URL
  // schema rejects a page below 1 before it could arrive — and it changes no
  // value the donor would produce.
  const pageSizeRatio = nextPageSize / currentPageSize;
  return Math.floor((Math.max(1, page) - 1) / pageSizeRatio) + 1;
}

/**
 * How the table's Usage column reads under the current sort.
 *
 * The value is `aria-sort`'s vocabulary, because that is what the header cell
 * has to announce and inventing a second spelling of it would only mean
 * translating at the call site.
 *
 * The three non-usage orderings are `"none"` rather than a direction: they say
 * nothing about usage, and claiming otherwise would tell a screen-reader user
 * the column is sorted when the rows are in flag or date order.
 */
export function usageSortDirection(sort: CatalogItemSort): SortDirection {
  if (sort === "most_used") return "descending";
  if (sort === "least_used") return "ascending";
  return "none";
}

/**
 * The sort a click on the Usage header moves to.
 *
 * Three states, not two: **most used → least used → Recommended**. The third
 * click clears the sort rather than flipping back to the first.
 *
 * That is the donor's, and it is a real capability rather than a nicety — it is
 * the only way back to the default order from the header. Its table sets
 * `enableSortingRemoval` (`react-ui/src/table/table.tsx:173`), so MRT's cycle is
 * `desc → asc → none`; on `none` it emits an empty sort array, and the list page
 * reads `event.sort[0] || {}`, finds no `field`, and runs
 * `searchParams.delete('sort')` (`items-list-page.tsx:225-231`). An absent
 * `sort` param is exactly how Recommended is spelled — here as there
 * (`catalogSortToUrl` returns `undefined` for it) — so the URL half of this
 * already worked; only the cycle was short.
 *
 * From an unrelated ordering the first click means "rank by usage", and the
 * useful end of that is the busiest items. That matches the select's own order
 * for the pair, and it is also MRT's: `usage_count` is numeric, so TanStack's
 * `getAutoSortDir()` returns `'desc'` and the first click is always descending.
 *
 * (The two-state version this replaces was defended on the grounds that a third
 * click "cannot land back on Newest" — true, and beside the point: the donor
 * lands on *Recommended*, which is a usage ordering's absence rather than
 * another field's presence.)
 *
 * The steps are the shared table's (`./sort-direction`); this only spells them
 * in the catalog's sort vocabulary.
 */
export function nextUsageSort(sort: CatalogItemSort): CatalogItemSort {
  const next = nextSortDirection(usageSortDirection(sort), "descending");
  if (next === "descending") return "most_used";
  if (next === "ascending") return "least_used";
  return DEFAULT_CATALOG_SORT;
}

/**
 * The longest term the search box accepts.
 *
 * Python's `SEARCH_MAX_LENGTH` refuses a longer one outright
 * (`server/app/use_cases/catalog/list_items.py`), and a refused request would
 * reach the browser as the generic scrubbed error — so the box stops the user
 * at the cap instead, and a hand-edited URL past it drops back to a plain
 * browse (`@/api/contracts/catalog`).
 */
export const CATALOG_SEARCH_TERM_MAX_LENGTH = 200;

/**
 * The search term as the URL should carry it, or nothing at all.
 *
 * Trimmed, because that is what the donor writes: both of its search boxes
 * `.trim()` before touching the address bar (`SearchInput.tsx`'s
 * `getValues('search').trim()`). Whitespace-only collapses to *absent* rather
 * than to `""`, which is the whole of the "an empty box behaves exactly like no
 * search" rule — one spelling of "no term", so the page cannot end up with two
 * URLs, two react-query keys, and two renderings of one browse view.
 *
 * Nothing else is touched. Quotes, parentheses and operator-looking words are
 * literal text to the ranking read, and sanitising them here would silently
 * change what the user asked for.
 */
/**
 * A search term's length, counted the way Python counts it.
 *
 * Two differences from `value.length`, and both are Python's doing
 * (`app/use_cases/catalog/list_items.py`):
 *
 *   - **Stripped first.** "Measured after stripping, because stripping is what
 *     decides whether there is a term at all" — 201 spaces is no term, not an
 *     over-long one.
 *   - **Code points, not UTF-16 units.** Python's `len` counts characters;
 *     JavaScript's `.length` counts the two halves of an astral character
 *     separately, so a box of emoji would be refused at half the real cap.
 *
 * Neither is a case anyone searching a materials catalog will hit. They are
 * fixed anyway because the alternative is caps that agree until they don't, and
 * the symptom then is a term the server would have taken being refused on the
 * way with nothing said. Every place that enforces the cap — both contract
 * faces and the box itself — measures with this.
 */
export const catalogSearchTermLength = (value: string) => [...value.trim()].length;

export function normalizeCatalogSearchTerm(term: string | undefined | null): string | undefined {
  const trimmed = term?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * The list's one URL write, as the options a router navigate takes.
 *
 * Shared rather than spelled out at the call site so that the route and the
 * tests that exercise the route's URL behaviour are running the *same* merge.
 * A test that reproduces this shape instead of calling it can only ever confirm
 * the copy, and the route is then free to drift out from under it.
 *
 * Two decisions live here:
 *
 *   - **Merged onto the current params, not replacing them.** A control writing
 *     its own key leaves its neighbours untouched — absent if never touched,
 *     explicit if touched earlier — which is the donor's `URLSearchParams.set`
 *     semantics. A key set to `undefined` still drops out, which is how the
 *     delete-when-empty params (`search`, `sort`, `category`, `mfrs`, `tiers`) clear
 *     themselves; the rest write their value, default included.
 *   - **A push by default, because that is what the donor's handlers do.** The
 *     pager, the page-size menu, the view toggle, the four radios, the
 *     deleted-items toggle, the EI Package select, the sort and the search term all take
 *     react-router's default in `items-list-page.tsx:152-303`, so Back walks
 *     the states the user actually looked at. Three writes are the exception
 *     and pass `{ replace: true }` themselves — the category tree
 *     (`CategoryFilter.tsx:61,67`), the manufacturer picker
 *     (`ManufacturersFilter.tsx:93,109`) and a tier chip's removal
 *     (`TiersFilter.tsx`'s `handleRemoveTierFilter`) — so Back from a narrowed list leaves
 *     the list. Making the page uniform would be tidier and would be a parity
 *     break; the asymmetry is the donor's.
 *
 *     A *correction* — the page rewriting a URL the user could not have meant —
 *     also asks to replace, because pushing one traps Back: it returns to the
 *     bad URL, which corrects itself forward again.
 */
export function catalogSearchNavigation<T extends object>(
  next: Partial<T>,
  options?: { replace?: boolean },
) {
  return {
    search: (previous: T) => ({ ...previous, ...next }),
    replace: options?.replace ?? false,
  };
}
